import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
    fetchAllCommentTags,
    fetchHighLowAnalysisScores,
    fetchScores,
    fetchScoresByCategory,
    fetchScoresByQuestion,
    fetchSurveyCommentQuestions,
    fetchSurveyCommentTags,
    fetchSurveyQuestionCorrelations,
    fetchSurveyScoresByAttributes,
    fetchSurveyScoresByCategoryXAttributes,
    fetchSurveyScoresByQuestionXAttributes,
    fetchSurveysAttributes,
} from '@/queries/surveys/system';
import { combineQueryErrors, groupAttributeOptionIdsBySetting, sortByScore } from '@/utils';

/** Shared */

export function useSurveyAttributes(organizationId: string, surveyId: string) {
    const attributesQuery = useQuery({
        queryKey: ['attributes', organizationId, surveyId],
        queryFn: async () => {
            const data = await fetchSurveysAttributes(organizationId, [surveyId]);
            return data[0] ?? null;
        },
    });

    return attributesQuery;
}

/** Summaries Tab */

export function useOverallScores(organizationId: string, surveyIdsToLoad: string[]) {
    return useQuery({
        queryKey: ['scores', organizationId, surveyIdsToLoad],
        queryFn: () => fetchScores(organizationId, { surveyIds: surveyIdsToLoad }),
        enabled: surveyIdsToLoad.length > 0,
    });
}

export function useScoresByCategory(organizationId: string, surveyIds: string[]) {
    return useQuery({
        queryKey: ['scores-by-category', organizationId, surveyIds],
        queryFn: () => fetchScoresByCategory(organizationId, { surveyIds }),
    });
}

export function useScoresByQuestion(organizationId: string, surveyIds: string[]) {
    return useQuery({
        queryKey: ['scores-by-question', organizationId, surveyIds],
        queryFn: () => fetchScoresByQuestion(organizationId, { surveyIds }),
    });
}

export function useRanking<K extends 'di' | 'deviation', T extends { [key in K]: number | null }>(
    key: K,
    items: T[],
    { excludeNull = true, limit = 3 } = {}
) {
    const filtered = excludeNull ? items.filter((item) => item[key] !== null) : items;
    const sorted = filtered.sort(sortByScore(key, 'desc'));
    return { best: sorted.slice(0, limit), worst: sorted.slice(-limit).reverse() };
}

export function useScoresByAttributes(
    organizationId: string,
    surveyId: string,
    settingId: string | null,
    previousSurveyId?: string | null
) {
    return useQuery({
        queryKey: ['survey-scores-by-attributes', organizationId, surveyId, settingId, previousSurveyId],
        queryFn: () => {
            const surveyIds = previousSurveyId ? [surveyId, previousSurveyId] : [surveyId];
            return fetchSurveyScoresByAttributes(organizationId, { surveyIds, settingId: settingId! });
        },
        enabled: !!settingId,
    });
}

export function useScoresByDepartment(organizationId: string, surveyId: string) {
    const attributesQuery = useSurveyAttributes(organizationId, surveyId);
    const departmentSettingId = attributesQuery.data?.attributes.find((a) => a.type === 'DEPARTMENT')?.id ?? null;
    const scoresByDepartmentQuery = useScoresByAttributes(organizationId, surveyId, departmentSettingId);

    return {
        scoresByDepartment: scoresByDepartmentQuery,
        ...combineQueryErrors(attributesQuery, scoresByDepartmentQuery),
    };
}

/** Categories Tab */

export function useScoresForCategoriesComparisonTable(
    organizationId: string,
    currentSurveyId: string,
    previousSurveyId: string | null,
    selectedOptionIds: string[]
) {
    const attributesQuery = useSurveyAttributes(organizationId, currentSurveyId);
    const attributes = groupAttributeOptionIdsBySetting(selectedOptionIds, attributesQuery.data?.attributes ?? []);

    const scoresByCategoryXAttributesQuery = useQuery({
        queryKey: ['scores-by-category-x-attributes', currentSurveyId, attributes],
        queryFn: () => fetchSurveyScoresByCategoryXAttributes(organizationId, currentSurveyId, { attributes }),
        // Keep the previous filter's rows on screen while the new ones load,
        // instead of flashing back to an empty/loading state.
        placeholderData: keepPreviousData,
    });

    const previousScoresByCategoryXAttributesQuery = useQuery({
        queryKey: ['scores-by-category-x-attributes', previousSurveyId, attributes],
        queryFn: () => fetchSurveyScoresByCategoryXAttributes(organizationId, previousSurveyId!, { attributes }),
        enabled: !!previousSurveyId,
        placeholderData: keepPreviousData,
    });

    return {
        scoresForCategoryComparisonTable: scoresByCategoryXAttributesQuery,
        previousScoresForCategoryComparisonTable: previousScoresByCategoryXAttributesQuery,
        ...combineQueryErrors(
            attributesQuery,
            scoresByCategoryXAttributesQuery,
            previousScoresByCategoryXAttributesQuery
        ),
    };
}

export function useScoresForAttributeComparisonChart(
    organizationId: string,
    currentSurveyId: string,
    selectedOptionIds: string[]
) {
    const attributesQuery = useSurveyAttributes(organizationId, currentSurveyId);
    const attributes = groupAttributeOptionIdsBySetting(selectedOptionIds, attributesQuery.data?.attributes ?? []);

    const scoresByCategoryXAttributesQuery = useQuery({
        queryKey: ['scores-by-category-x-attributes', currentSurveyId, attributes],
        queryFn: () => fetchSurveyScoresByCategoryXAttributes(organizationId, currentSurveyId, { attributes }),
        // Keep the previous filter's data on screen while the new fetch is in
        // flight, so Chart.js animates directly between the two datasets.
        placeholderData: keepPreviousData,
    });

    return {
        scoresForAttributeComparisonChart: scoresByCategoryXAttributesQuery,
        ...combineQueryErrors(attributesQuery, scoresByCategoryXAttributesQuery),
    };
}

/** Questions Tab */

export function useScoresForQuestionsComparisonTable(
    organizationId: string,
    currentSurveyId: string,
    previousSurveyId: string | null,
    selectedOptionIds: string[]
) {
    const attributesQuery = useSurveyAttributes(organizationId, currentSurveyId);
    const attributes = groupAttributeOptionIdsBySetting(selectedOptionIds, attributesQuery.data?.attributes ?? []);

    const scoresByQuestionXAttributesQuery = useQuery({
        queryKey: ['scores-by-question-x-attributes', organizationId, currentSurveyId, attributes],
        queryFn: () => fetchSurveyScoresByQuestionXAttributes(organizationId, currentSurveyId, { attributes }),
        // Keep the previous filter's rows on screen while the new ones load,
        // instead of flashing back to an empty/loading state.
        placeholderData: keepPreviousData,
    });

    const previousScoresByQuestionXAttributesQuery = useQuery({
        queryKey: ['scores-by-question-x-attributes', organizationId, previousSurveyId, attributes],
        queryFn: () => fetchSurveyScoresByQuestionXAttributes(organizationId, previousSurveyId!, { attributes }),
        enabled: !!previousSurveyId,
        placeholderData: keepPreviousData,
    });

    return {
        scoresForQuestionsComparisonTable: scoresByQuestionXAttributesQuery,
        previousScoresForQuestionsComparisonTable: previousScoresByQuestionXAttributesQuery,
        ...combineQueryErrors(
            attributesQuery,
            scoresByQuestionXAttributesQuery,
            previousScoresByQuestionXAttributesQuery
        ),
    };
}

export function useHighLowAnalysisScores(organizationId: string, surveyId: string, settingId: string | null) {
    const attributesQuery = useSurveyAttributes(organizationId, surveyId);

    const scoresQuery = useQuery({
        queryKey: ['high-low-analysis-scores', organizationId, surveyId, settingId],
        queryFn: () => fetchHighLowAnalysisScores(organizationId, surveyId, settingId!),
        enabled: !!settingId,
        // Keep the previous setting's chart on screen while the new one loads,
        // instead of flashing back to an empty/loading state.
        placeholderData: keepPreviousData,
    });

    return {
        data: scoresQuery.data,
        isFetching: scoresQuery.isFetching,
        ...combineQueryErrors(attributesQuery, scoresQuery),
    };
}

export function useSurveyQuestionCorrelations(
    organizationId: string,
    surveyId: string,
    questionId: string | null,
    selectedOptionIds: string[]
) {
    const attributesQuery = useSurveyAttributes(organizationId, surveyId);
    const attributes = groupAttributeOptionIdsBySetting(selectedOptionIds, attributesQuery.data?.attributes ?? []);

    const correlationsQuery = useQuery({
        queryKey: ['question-correlations', organizationId, surveyId, questionId, attributes],
        queryFn: () => fetchSurveyQuestionCorrelations(organizationId, surveyId, questionId!, { attributes }),
        enabled: !!questionId,
    });

    return {
        data: correlationsQuery.data,
        isFetching: correlationsQuery.isFetching,
        ...combineQueryErrors(attributesQuery, correlationsQuery),
    };
}

/** Comments Tab */

export function useSurveyCommentQuestions(organizationId: string, surveyId: string) {
    return useQuery({
        queryKey: ['surveyCommentQuestions', organizationId, surveyId],
        queryFn: () => fetchSurveyCommentQuestions(organizationId, surveyId),
        select: (data) => data.questions,
    });
}

/** The whole tag catalogue, for assigning tags the analysis never produced. */
export function useAllCommentTags() {
    return useQuery({
        queryKey: ['commentTags'],
        queryFn: fetchAllCommentTags,
    });
}

export function useCommentTags(organizationId: string, surveyId: string) {
    return useQuery({
        queryKey: ['surveyCommentTags', organizationId, surveyId],
        queryFn: () => fetchSurveyCommentTags(organizationId, surveyId),
    });
}
